import { Button, Group, Image, Stack, Text } from "@mantine/core";
import classNames from "classnames";
import type { ChangeEvent, ReactNode } from "react";
import { useState } from "react";
import { deleteFile } from "../../../../../utils/files/deleteFile";
import { uploadFileOrFiles } from "../../../../../utils/files/uploadFileOrFiles";
import { camelize } from "../../../../../utils/SnakeCamelConversion";
import type { CommonFormFieldItemProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { FormFieldRightSection } from "../FormFieldRightSection";
import type { FormFieldTextInputProps } from "./FormFieldTextInput";

interface FormFieldUploadableImageProps extends CommonFormFieldItemProps {
  accept?: string;
  className?: string;
  defaultValue?: string | null;
  disabled?: boolean;
  imageType?: string;
  label?: FormFieldTextInputProps["label"];
  name?: string;
  placeholder?: string;
  removeButtonText?: string;
  required?: FormFieldTextInputProps["required"];
  resourceId?: number;
  resourceImageFieldName: string;
  rightSection?: ReactNode;
  type: "uploadable-image";
  value?: string | null;
  deleteUrl?: (resourceId: number, fieldName: string) => string;
  onChange?: (value: string | null) => void;
  setErrorTexts?: (errors: Array<string>) => void;
  uploadUrl?: (resourceId: number, fieldName: string) => string;
}

/**
 * Uploadable image form field that handles image file selection and display.
 * Creates temporary URLs for local file preview.
 */
function FormFieldUploadableImage({
  accept = "image/*",
  className,
  defaultValue,
  disabled,
  helpText,
  highlight,
  imageType = "image",
  isBetaFeature,
  label,
  name,
  placeholder = "Keine Datei ausgewählt",
  removeButtonText = "Entfernen",
  required,
  resourceId,
  resourceImageFieldName,
  rightSection,
  value,
  warned,
  deleteUrl,
  onChange,
  setErrorTexts,
  uploadUrl
}: FormFieldUploadableImageProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setErrorTexts?.([]);

    if (file) {
      setSelectedFile(file);

      if (uploadUrl && resourceId !== undefined) {
        setUploading(true);

        try {
          const uploadedUrl = await uploadImage(file);
          onChange?.(uploadedUrl);
        } catch (error) {
          setErrorTexts?.(error.response?.data[camelize(imageType)] ?? []);
        } finally {
          setUploading(false);
        }
      } else {
        const fileUrl = URL.createObjectURL(file);
        onChange?.(fileUrl);
      }
    }
  };

  async function handleRemove() {
    if (
      deleteUrl &&
      resourceId !== undefined &&
      value &&
      !value.startsWith("blob:")
    ) {
      setUploading(true);
      try {
        await deleteImage();
        setSelectedFile(null);
        onChange?.(null);
      } catch (error) {
        setErrorTexts?.(error.response?.data[camelize(imageType)] ?? []);
      } finally {
        setUploading(false);
      }
    } else {
      setSelectedFile(null);
      onChange?.(null);
    }

    const input = document.getElementById(
      `upload-input-${name || "default"}`
    ) as HTMLInputElement;

    if (input) {
      input.value = "";
    }
  }

  function handleClickUpload() {
    const input = document.getElementById(`upload-input-${name || "default"}`);

    if (input) {
      input.click();
    }
  }

  async function uploadImage(file: File) {
    if (!uploadUrl || resourceId === undefined) {
      throw new Error(
        "Upload URL and resource ID are required for server upload"
      );
    }

    const url = uploadUrl(resourceId, resourceImageFieldName);
    const response = await uploadFileOrFiles<string>(file, url, imageType);

    return response.data;
  }

  async function deleteImage() {
    if (!deleteUrl || resourceId === undefined) {
      throw new Error(
        "Delete URL and resource ID are required for server delete"
      );
    }

    const url = deleteUrl(resourceId, resourceImageFieldName);
    const response = await deleteFile<string>(url, imageType);

    return response.data;
  }

  const displayValue = value || defaultValue;
  const hasImage = !!displayValue;

  return (
    <Stack className="FormField" gap="xs" mb="sm">
      <div className={classNames("FormFieldUploadableImage", className)}>
        {label && (
          <FormFieldLabel
            helpText={helpText}
            highlight={highlight}
            isBetaFeature={isBetaFeature}
            label={label}
            required={required}
          />
        )}

        <Stack align="flex-start" gap="sm">
          {hasImage && (
            <Image
              alt="Hochgeladenes Bild"
              className="uploaded-image"
              fit="contain"
              h={200}
              maw={700}
              mb={5}
              src={displayValue}
              style={{ objectFit: "contain" }}
            />
          )}

          <Group gap="xs">
            <Button
              color="primary"
              disabled={disabled}
              loading={uploading}
              onClick={handleClickUpload}
            >
              Datei auswählen
            </Button>

            {hasImage && (
              <Button
                color="error"
                disabled={disabled}
                ml={7}
                onClick={handleRemove}
              >
                {removeButtonText}
              </Button>
            )}

            <input
              accept={accept}
              hidden
              id={`upload-input-${name || "default"}`}
              type="file"
              onChange={handleFileSelect}
            />

            {selectedFile ? (
              <Text c="dimmed" size="sm">
                {selectedFile.name}
              </Text>
            ) : !hasImage ? (
              <Text c="dimmed" size="sm">
                {placeholder}
              </Text>
            ) : null}
          </Group>
        </Stack>
        <FormFieldRightSection rightSection={rightSection} warned={warned} />
      </div>
    </Stack>
  );
}

export { FormFieldUploadableImage, FormFieldUploadableImageProps };
